/**
 * EmployeeDetailModal — Detalle completo de un empleado: datos, contrato, previsional, liquidaciones.
 */
import { useState } from 'react'
import { Pencil, Briefcase, Shield, FileText, Calendar, Clock, Heart, Gift, Download, FolderOpen, KeyRound, Loader2, Mail, RefreshCw } from 'lucide-react'
import EmployeeDocumentsTab from './EmployeeDocumentsTab'
import EmployeeLeavesTab from './EmployeeLeavesTab'
import VacationRecordsCard from './VacationRecordsCard'
import {
  useEmployee, useContracts, usePayslips, useVacationBalance, usePrevisionalConfigs, useEmployeeBonuses,
  usePortalAccountStatus, useInvitePortal, useDisablePortal, useResetPortalPassword,
  useVacationRequests,
  type Employee,
} from '../hooks/usePayroll'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { VACATION_REQUEST_STATUS } from '../../../components/ds/statusCatalogs'
import { Modal } from '@/components/ds/Modal'
import { EmployeePhotoField } from './EmployeePhotoField'
import { AlertasDelEmpleado } from './AlertasDelEmpleado'

interface Props {
  employeeId: string
  onClose: () => void
  onEdit: (employee: Partial<Employee>) => void
}

type Tab = 'info' | 'vacations' | 'leaves' | 'documents'

export default function EmployeeDetailModal({ employeeId, onClose, onEdit }: Props) {
  const locale = useTenantLocale()
  const [activeTab, setActiveTab] = useState<Tab>('info')
  const { data: employee, isLoading } = useEmployee(employeeId)
  const { data: contractsData } = useContracts({ employee_id: employeeId, current: 'true' })
  const { data: previsionalData } = usePrevisionalConfigs({ employee_id: employeeId, current: 'true' })
  const { data: bonusesData } = useEmployeeBonuses({ employee_id: employeeId })
  const { data: payslipsData } = usePayslips({ employee_id: employeeId, page: 1 })
  const { data: vacationData } = useVacationBalance(employeeId)
  const { can } = usePermission()
  // Toda la pestaña de vacaciones exige `payroll.vacations`, que la ficha no
  // requiere (esa se ve con `payroll.employees`). Y no es solo el historial:
  // `VacationBalanceView` pide el mismo módulo, así que sin el permiso también
  // falla el saldo. Sin él la pestaña no se ofrece y no se dispara ninguna
  // petición — un 403 se vería igual que «no tiene vacaciones» (#968).
  const puedeVerVacaciones = can('payroll.vacations', 'view')
  const { data: vacationRequests } = useVacationRequests(
    { employee_id: employeeId }, { enabled: !!employeeId && puedeVerVacaciones })


  if (!employeeId) return null

  const contract = contractsData?.contracts?.[0]
  const previsional = previsionalData?.configs?.[0]
  const bonuses = bonusesData?.assignments || []
  const payslips = payslipsData?.payslips || []
  const vacationDays = vacationData?.total_days_pending || '0'

  // `fill` = alto FIJO, no máximo: con un máximo el modal se dimensionaba por su
  // contenido y cambiaba de tamaño al alternar de pestaña o de carpeta (una
  // carpeta con 15 documentos lo estiraba y otra con 1 lo encogía). Con alto
  // declarado, los hijos `flex-1 min-h-0` reparten ESE alto, que es lo que hace
  // que el scroll de cada pestaña funcione de verdad.
  // El ancho llega a 1280px porque los nombres de archivo de RRHH son largos
  // (`2026 07-01 Certificado de Vacaciones Nombre Apellido (2)`) y a 1024px la
  // columna del nombre se partía en tres líneas.
  return (
    <Modal
      open
      onClose={onClose}
      size="2xl"
      title="Detalle del Empleado"
      fill
      style={{ maxWidth: 1280 }}
      actions={
        <>
          {employee && (
            <button onClick={() => onEdit(employee)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
              <Pencil size={12} aria-hidden="true" /> Editar
            </button>
          )}
        </>
      }
    >
      <div className="flex border-b border-divider shrink-0 px-5">
        <button
          onClick={() => setActiveTab('info')}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'info'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'
          }`}
        >
          <Briefcase size={13} aria-hidden="true" /> Información
        </button>
        {puedeVerVacaciones && (
          <button
            onClick={() => setActiveTab('vacations')}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'vacations'
                ? 'border-primary text-primary'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            <Calendar size={13} aria-hidden="true" /> Vacaciones
          </button>
        )}
        {/* Mismo gate que Vacaciones: los permisos viven en `payroll.vacations`
            y los resuelve la misma persona. Sin el gate, la pestaña dispararía
            una petición que vuelve 403 y se vería como «no tiene permisos». */}
        {puedeVerVacaciones && (
          <button
            onClick={() => setActiveTab('leaves')}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'leaves'
                ? 'border-primary text-primary'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            <Clock size={13} aria-hidden="true" /> Permisos
          </button>
        )}
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'documents'
              ? 'border-primary text-primary'
              : 'border-transparent text-content-muted hover:text-content'
          }`}
        >
          <FolderOpen size={13} aria-hidden="true" /> Documentos
        </button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-content-subtle">Cargando...</div>
      ) : employee ? (
        <>
        {activeTab === 'documents' ? (
          /* La cadena flex tiene que ser CONTINUA hasta la tabla, o el scroll
             no aparece y la lista se sale del modal (medido: la tabla quedaba
             en 704px dentro de un contenedor de 628px).
             - `min-h-0`: deja que este hijo se contraiga al alto del modal en
               vez de crecer con el contenido.
             - `flex flex-col`: sin esto, el `h-full` del tab hijo resuelve
               contra una altura derivada de `flex-grow` y el navegador lo trata
               como `auto` — ahí se rompía la cadena.
             Ya NO lleva `h-[70vh]`: con el modal en alto fijo, el alto sale de
             `flex-1`. La altura propia era justamente lo que hacía que el modal
             midiera distinto según cuántos documentos tuviera la carpeta. */
          <div className="flex-1 min-h-0 overflow-hidden p-5 flex flex-col">
            <EmployeeDocumentsTab employeeId={employeeId} />
          </div>
        ) : activeTab === 'leaves' ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-5">
            <EmployeeLeavesTab employeeId={employeeId} locale={locale} />
          </div>
        ) : activeTab === 'vacations' ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-6">
            <VacacionesCard
              saldo={vacationDays}
              porTipo={vacationData?.by_type}
              bolsas={vacationData?.records || []}
              solicitudes={vacationRequests?.requests}
              locale={locale}
            />
            {/* De dónde sale ese saldo. Va debajo del historial porque se
                consulta menos: lo habitual es mirar los días, no el período
                que los genera. */}
            <VacationRecordsCard
              employeeId={employeeId}
              locale={locale}
              hireDate={employee.hire_date}
            />
          </div>
        ) : (
        /* Mismo reparto que el tab de Documentos para que las dos pestañas
           ocupen exactamente el mismo alto. */
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-6">
          {/* La foto (la sube RRHH, la ve toda la organización) y, al lado, las
              novedades de un vistazo: de vacaciones, con licencia, contrato por
              vencer… Las calcula el backend con los criterios de las pestañas. */}
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="flex-shrink-0">
              <EmployeePhotoField
                employeeId={employeeId}
                nombre={employee.full_name || `${employee.first_name} ${employee.last_name}`}
                fotoUrl={employee.photo_url}
              />
            </div>
            <div className="flex-1 min-w-0 sm:max-w-md">
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-2">
                Novedades
              </h3>
              <AlertasDelEmpleado alertas={employee.alertas} />
            </div>
          </div>
          {/* Datos personales */}
          <div>
            <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Briefcase size={12} /> Datos Personales
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Nombre" value={employee.full_name || `${employee.first_name} ${employee.last_name}`} />
              <Field label="RUT" value={employee.rut} mono />
              <Field label="Estado" value={
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${
                  employee.status === 'active' ? 'bg-success-surface-strong text-success-content' :
                  employee.status === 'on_leave' ? 'bg-warning-surface-strong text-warning-content' :
                  'bg-danger-surface-strong text-danger-content'
                }`}>
                  {employee.status === 'active' ? 'Activo' : employee.status === 'on_leave' ? 'Con Licencia' : 'Desvinculado'}
                </span>
              } />
              <Field label="Fecha de Nacimiento" value={employee.birth_date ? formatDate(employee.birth_date, locale) : '—'} />
              <Field label="Género" value={employee.gender === 'M' ? 'Masculino' : employee.gender === 'F' ? 'Femenino' : employee.gender || '—'} />
              <Field label="Estado Civil" value={employee.marital_status || '—'} />
              <Field label="Nacionalidad" value={employee.nationality || '—'} />
              <Field label="Teléfono" value={employee.phone || '—'} />
              <Field label="Email" value={employee.email || '—'} />
              <Field label="Dirección" value={employee.address || '—'} span2 />
              <Field label="Fecha Ingreso" value={formatDate(employee.hire_date, locale)} />
              {employee.termination_date && <Field label="Fecha Desvinculación" value={formatDate(employee.termination_date, locale)} />}
            </div>
          </div>

          {/* Contrato vigente */}
          {contract && (
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FileText size={12} /> Contrato Vigente
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Tipo" value={contract.contract_type_display} />
                <Field label="Cargo" value={contract.position || '—'} />
                <Field label="Departamento" value={contract.department || '—'} />
                <Field label="Sueldo Base" value={`$${parseFloat(contract.base_salary).toLocaleString('es-CL')}`} highlight />
                <Field label="Inicio" value={formatDate(contract.start_date, locale)} />
                <Field label="Término" value={contract.end_date ? formatDate(contract.end_date, locale) : 'Indefinido'} />
                <Field label="Horas Semanales" value={`${contract.work_hours_weekly}h`} />
              </div>
            </div>
          )}

          {/* Config previsional */}
          {previsional && (
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Heart size={12} /> Configuración Previsional
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="AFP" value={previsional.afp_display} />
                <Field label="Tasa AFP" value={`${previsional.afp_rate}%`} />
                <Field label="Salud" value={previsional.health_system_display} />
                {previsional.health_system === 'isapre' && (
                  <>
                    <Field label="Isapre" value={previsional.isapre_display || previsional.isapre_name || '—'} />
                    <Field label="Plan (UF)" value={`${previsional.isapre_plan_uf} UF`} highlight />
                    {previsional.isapre_fun_url && (
                      <div>
                        <p className="text-xs text-content-subtle font-medium mb-0.5">FUN Isapre</p>
                        <a href={previsional.isapre_fun_url} target="_blank" rel="noopener"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          <Download size={10} /> Descargar FUN
                        </a>
                      </div>
                    )}
                  </>
                )}
                {parseFloat(previsional.apv_amount) > 0 && (
                  <>
                    <Field label="APV Mensual" value={`$${parseFloat(previsional.apv_amount).toLocaleString()}`} />
                    <Field label="Régimen APV" value={previsional.apv_regime === 'A' ? 'Régimen A' : previsional.apv_regime === 'B' ? 'Régimen B' : '—'} />
                  </>
                )}
              </div>
            </div>
          )}
          {!previsional && (
            <div className="p-3 bg-warning-surface border border-warning-border rounded-lg text-xs text-warning-content">
              Aún no hay configuración previsional para este empleado. Configura AFP, salud y mutual al editar su contrato de trabajo.
            </div>
          )}

          {/* Bonos asignados */}
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Gift size={12} /> Bonos Asignados ({bonuses.length})
              </h3>
              <DataTable<any>
                columns={[
                  { key: 'bonus_name', header: 'Bono', sortable: true,
                    render: b => <span className="text-content">{b.bonus_name}</span> },
                  { key: 'bonus_code', header: 'Código', sortable: true,
                    render: b => <span className="text-xs text-content-subtle font-mono">{b.bonus_code}</span> },
                  { key: 'effective_amount', header: 'Monto', numeric: true, sortable: true,
                    sortValue: b => Number(b.effective_amount),
                    render: b => (
                      <span className="font-medium">${parseFloat(b.effective_amount).toLocaleString()}</span>
                    ) },
                  { key: 'is_imponible', header: 'Imponible', sortable: true,
                    sortValue: b => (b.is_imponible ? 1 : 0),
                    render: b => (b.is_imponible ? '✓' : '—') },
                  { key: 'is_taxable', header: 'Tributable', sortable: true,
                    sortValue: b => (b.is_taxable ? 1 : 0),
                    render: b => (b.is_taxable ? '✓' : '—') },
                ]}
                rows={bonuses}
                rowKey={b => b.id}
                minWidth={560}
                surface={false}
                caption="Bonos asignados al trabajador"
              />
            </div>

          {/* Las vacaciones viven en su propia pestaña: en Información
              quedaban al fondo de una página larga y había que bajar hasta
              ahí para ver el saldo de alguien. */}

          {/* Acceso al portal del colaborador */}
          <PortalAccessCard employeeId={employeeId} employeeEmail={employee.email} />

          {/* Últimas liquidaciones */}
          {payslips.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Shield size={12} /> Últimas Liquidaciones
              </h3>
              <DataTable<any>
                columns={[
                  { key: 'period_label', header: 'Período', sortable: true,
                    render: ps => <span className="text-content-muted">{ps.period_label || '—'}</span> },
                  { key: 'gross_salary', header: 'Bruto', numeric: true, sortable: true,
                    sortValue: ps => Number(ps.gross_salary),
                    render: ps => `$${parseFloat(ps.gross_salary).toLocaleString('es-CL')}` },
                  { key: 'total_deductions', header: 'Descuentos', numeric: true, sortable: true,
                    sortValue: ps => Number(ps.total_deductions),
                    render: ps => (
                      <span className="text-danger-solid">
                        ${parseFloat(ps.total_deductions).toLocaleString('es-CL')}
                      </span>
                    ) },
                  { key: 'net_salary', header: 'Líquido', numeric: true, sortable: true,
                    sortValue: ps => Number(ps.net_salary),
                    render: ps => (
                      <span className="font-semibold text-primary">
                        ${parseFloat(ps.net_salary).toLocaleString('es-CL')}
                      </span>
                    ) },
                  { key: 'status', header: 'Estado', sortable: true,
                    render: ps => (
                      <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                        ps.status === 'paid' ? 'bg-success-surface-strong text-success-content' :
                        ps.status === 'approved' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                        'bg-surface-muted text-content-muted'
                      }`}>{ps.status}</span>
                    ) },
                ]}
                rows={payslips.slice(0, 6)}
                rowKey={ps => ps.id}
                minWidth={560}
                surface={false}
                caption="Últimas liquidaciones del trabajador"
              />
            </div>
          )}
        </div>
        )}
        </>
      ) : (
        <div className="p-8 text-center text-sm text-content-subtle">Empleado no encontrado.</div>
      )}
    </Modal>
  )
}


/**
 * Días que el saldo declara como tomados y que no tienen una solicitud con
 * fechas detrás.
 *
 * 🔴 No es un error: en un tenant migrado, parte de los días tomados entró como
 * acumulado inicial sin desglose —en zenlatam son 746 días de 8 personas, todas
 * antiguas—. Decirlo evita el malentendido que reportó RRHH al comparar contra
 * el sistema anterior: sin este aviso, el historial parece incompleto y se
 * concluye que faltan cargar días que en realidad están contados.
 */
export function diasSinFecha(bolsas: any[], solicitudes?: any[]): number {
  if (!solicitudes) return 0
  const tomados = bolsas.reduce((s, b) => s + parseFloat(b.days_taken || 0), 0)
  const conFecha = solicitudes
    .filter((r: any) => r.status === 'taken' || r.status === 'approved')
    .reduce((s: number, r: any) => s + parseFloat(r.days_requested || 0), 0)
  return Math.round((tomados - conFecha) * 100) / 100
}

function VacacionesCard({ saldo, porTipo, bolsas, solicitudes, locale }: {
  saldo: string
  porTipo?: Record<string, any>
  bolsas: any[]
  solicitudes?: any[]
  locale: string
}) {
  // Más recientes primero. El historial completo en orden cronológico —para
  // contrastarlo contra otro sistema— está en Nómina → Vacaciones, que tiene
  // filtro por trabajador y selector de orden.
  const historial = [...(solicitudes || [])].sort(
    (a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
  const sinFecha = diasSinFecha(bolsas, solicitudes)

  return (
    <div>
      <div className="p-3 bg-success-surface border border-emerald-100 dark:border-emerald-900/50 rounded-lg">
        <span className="text-2xl font-bold text-success-content">
          {parseFloat(saldo).toLocaleString('es-CL')}
        </span>
        <span className="text-sm text-success-content-soft ml-2">días pendientes</span>
        {porTipo && (
          <div className="mt-1.5 flex flex-wrap gap-x-4 text-xs text-success-content">
            {Object.entries(porTipo).map(([k, v]: [string, any]) => (
              <span key={k}>
                {v.label}: <strong>{parseFloat(v.days_pending || 0).toLocaleString('es-CL')}</strong>
              </span>
            ))}
          </div>
        )}
      </div>

      {historial.length > 0 && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-bold text-content-subtle uppercase tracking-wider">
              Historial
            </span>
            <span className="text-xs text-content-subtle">
              {historial.length} registro{historial.length === 1 ? '' : 's'}
            </span>
          </div>
          <DataTable<any>
            columns={[
              { key: 'start_date', header: 'Desde', sortable: true,
                render: r => <span className="text-content-muted">{formatDate(r.start_date, locale)}</span> },
              { key: 'end_date', header: 'Hasta', sortable: true,
                render: r => <span className="text-content-muted">{formatDate(r.end_date, locale)}</span> },
              { key: 'days_requested', header: 'Días', numeric: true, sortable: true,
                sortValue: r => Number(r.days_requested),
                render: r => <span className="font-medium">{r.days_requested}</span> },
              { key: 'record_vacation_type_display', header: 'Bolsa', sortable: true,
                render: r => (
                  <span className="text-content-muted">{r.record_vacation_type_display || '—'}</span>
                ) },
              { key: 'status', header: 'Estado', sortable: true,
                render: r => <StatusBadge status={r.status} catalog={VACATION_REQUEST_STATUS} /> },
            ]}
            rows={historial}
            rowKey={r => r.id}
            minWidth={560}
            surface={false}
            caption="Vacaciones tomadas por el trabajador"
          />
        </div>
      )}

      {sinFecha > 0.5 && (
        <p className="mt-2 text-xs text-content-subtle">
          Hay <strong>{sinFecha.toLocaleString('es-CL')}</strong> días tomados que no tienen fecha
          registrada. Están descontados del saldo y vienen de la carga inicial del sistema anterior,
          que entregó el acumulado sin el detalle de cada período.
        </p>
      )}
    </div>
  )
}

function PortalAccessCard({ employeeId, employeeEmail }: { employeeId: string; employeeEmail?: string }) {
  const { data, isLoading } = usePortalAccountStatus(employeeId)
  const invite = useInvitePortal(employeeId)
  const disable = useDisablePortal(employeeId)
  const reset = useResetPortalPassword(employeeId)

  const inFlight = invite.isPending || disable.isPending || reset.isPending

  let badge: React.ReactNode = null
  let primary: React.ReactNode = null

  if (isLoading) {
    badge = <span className="text-xs text-content-subtle">Cargando…</span>
  } else if (!data) {
    badge = <span className="text-xs text-content-subtle">—</span>
  } else if (data.has_erp_user) {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
        Acceso vía cuenta ERP
      </span>
    )
  } else if (!data.has_account) {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-surface-muted text-content-muted">
        Sin invitar
      </span>
    )
    primary = (
      <button
        onClick={() => invite.mutate()}
        disabled={!employeeEmail || inFlight}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
      >
        {invite.isPending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
        Invitar al portal
      </button>
    )
  } else if (!data.is_active) {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-danger-surface-strong text-danger-content">
        Desactivado
      </span>
    )
    primary = (
      <button
        onClick={() => invite.mutate()}
        disabled={inFlight}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
      >
        {invite.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        Reactivar
      </button>
    )
  } else if (!data.is_activated) {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-warning-surface-strong text-warning-content">
        Invitación pendiente
      </span>
    )
    primary = (
      <button
        onClick={() => invite.mutate()}
        disabled={inFlight}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-50"
      >
        {invite.isPending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
        Reenviar invitación
      </button>
    )
  } else {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-success-surface-strong text-success-content">
        Activo
      </span>
    )
  }

  return (
    <div>
      <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <KeyRound size={12} /> Acceso al Portal del Colaborador
      </h3>
      <div className="border border-divider rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-content-subtle font-medium">Estado:</span>
              {badge}
            </div>
            {data?.email && (
              <p className="text-xs text-content-muted truncate">
                Email: <span className="text-content">{data.email}</span>
              </p>
            )}
            {data?.last_login_at && (
              <p className="text-xs text-content-subtle">
                Último ingreso: {new Date(data.last_login_at).toLocaleString()}
              </p>
            )}
            {!employeeEmail && !data?.has_erp_user && (
              <p className="text-xs text-warning-content-soft">
                El empleado no tiene email registrado. Cárgalo antes de invitar.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {primary}
            {data?.has_account && data.is_active && data.is_activated && (
              <button
                onClick={() => reset.mutate()}
                disabled={inFlight}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content-muted border border-divider rounded-lg hover:bg-soft disabled:opacity-50"
              >
                {reset.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Reset contraseña
              </button>
            )}
            {data?.has_account && data.is_active && (
              <button
                onClick={() => {
                  if (window.confirm('¿Desactivar el acceso al portal para este empleado?')) {
                    disable.mutate()
                  }
                }}
                disabled={inFlight}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface disabled:opacity-50"
              >
                Desactivar
              </button>
            )}
          </div>
        </div>
        {(invite.isSuccess || reset.isSuccess || disable.isSuccess) && (
          <p className="text-xs text-success-content">
            {(invite.data as any)?.detail || (reset.data as any)?.detail || (disable.data as any)?.detail}
          </p>
        )}
      </div>
    </div>
  )
}

function Field({ label, value, mono, highlight, span2 }: {
  label: string; value: React.ReactNode; mono?: boolean; highlight?: boolean; span2?: boolean
}) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <p className="text-xs text-content-subtle font-medium mb-0.5">{label}</p>
      <p className={`text-sm ${highlight ? 'text-primary font-bold' : 'text-content'} ${mono ? 'font-mono' : ''}`}>
        {value}
      </p>
    </div>
  )
}

